import { useMemo } from 'react'
import {
  organizationDetailsSchema,
  OrganizationDetails
} from '@/features/organization/api/OrganizationModel'
import { Outlet, useNavigate } from 'react-router-dom'
import { useSession } from '@/shared/hooks/useSession'
import { Logo, Step } from '@/common'
import OperatorLanguageSelector from '@/shared/components/LanguageSelector'
import { useLogout } from '@/features/auth/hooks/useAuth'
import useI18n from '@/shared/hooks/useI18n'
import OnboardingUserMenu from '@/components/OnboardingUserMenu'
import { LoadingScreen } from '@/shared/components'

export default function OnboardingLayout({ state }: { state: OrganizationDetails }) {
  const { t } = useI18n()
  const { session } = useSession()
  const { mutateAsync: logout } = useLogout()

  const location = window.location.pathname

  const navigate = useNavigate()
  const steps = useMemo(
    () => [
      {
        id: 'emailVerification',
        title: t('onboarding:steps.emailVerification'),
        path: '/email-verification',
        isComplete: session?.isEmailVerified
      },
      {
        id: 'organizationDetails',
        title: t('onboarding:steps.organizationDetails'),
        path: '/organization-details',
        isComplete: organizationDetailsSchema.safeParse({
          placeOfRegistration: state.placeOfRegistration,
          organizationName: state.organizationName
        }).success
      }
    ],
    [t, session, state]
  )

  const getStepState = (path: string, isComplete: boolean) => {
    if (isComplete) {
      return 'completed'
    }

    if (location.includes(path)) {
      return 'active'
    }

    return 'inactive'
  }

  const isSuccessPage = location.includes('success')

  if (!session) {
    return <LoadingScreen />
  }

  return (
    <div className="flex flex-col h-full relative">
      <div className="h-16 absolute top-8 left-8 right-8 space-y-2 sm:flex items-center space-x-2 justify-between">
        <div className="flex items-baseline gap-2">
          <Logo className="w-32" />
          <span className="text-gray-700 uppercase font-semibold">Operator</span>
        </div>
        <div className="w-fit">
          <OnboardingUserMenu
            email={session.email}
            fullName={session.firstName + ' ' + session.lastName}
            onLogout={logout}
          />
        </div>
      </div>
      <div className="grow flex flex-col justify-center items-center">
        <div className="max-w-2xl w-full mx-auto max-sm:px-4">
          <Outlet />
        </div>
      </div>
      {!isSuccessPage && (
        <footer className="w-full bg-white mt-auto sticky bottom-0 border h-16 flex justify-center">
          <div className="absolute max-sm:relative top-0 h-full right-0 flex items-center px-6">
            <OperatorLanguageSelector showLabel />
          </div>
          {steps.map((step, index) => {
            const stepState = getStepState(step.path, step.isComplete)
            return (
              <button
                key={index}
                onClick={() => navigate(step.path)}
                disabled={stepState === 'inactive' || stepState === 'active'}
                className="px-6"
              >
                <Step className="h-full" state={stepState}>
                  {step.title}
                </Step>
              </button>
            )
          })}
        </footer>
      )}
    </div>
  )
}
